import { AmountSuperScript } from "components/design/AmountSuperScript";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { TableUI } from "components/design/TableUI";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { ArrowLightLine } from "components/icons/ArrowLightLine";
import { ConditionalLink } from "components/conditionalLink";
import { DISPLAY_DATE } from "constants/date";
import { FINANCIAL_RECORD_TYPE } from "constants/vendorBills";
import dayjs from "dayjs";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useMemo, useState } from "react";
import {
  createColumnHelper,
  getCoreRowModel,
  Row,
  useReactTable,
} from "react-table-8.10.7";
import AIGenerateButtonStar from "static/images/AIGenerateButtonStar.svg";
import TicketResolve from "static/images/TicketResolve.svg";
import { useGetSuggestedInvoicesQuery } from "store/apis/transactions";
import { SuggestedInvoice } from "types/Models/invoice";
import { VendorBill } from "types/Models/vendorBills";
import { getUsdPendingAmount } from "utils/getUsdPendingAmount";
import { pluralize } from "utils/pluralize";

type Props = {
  transactionId: string;
  selectedBills: VendorBill[];
  setSelectedBills: React.Dispatch<React.SetStateAction<VendorBill[]>>;
  enableSelection: boolean;
};

const columnHelper = createColumnHelper<SuggestedInvoice>();

const SUGGESTED_ACCORDION_VALUE = "SUGGESTED";

export const SuggestedBillsAccordion = ({
  transactionId,
  selectedBills,
  setSelectedBills,
  enableSelection,
}: Props) => {
  const entityId = useCurrentEntityId();
  const { link } = useConstructInternalLink();
  const [accordionValue, setAccordionValue] = useState<string | undefined>(
    SUGGESTED_ACCORDION_VALUE
  );

  const { data } = useGetSuggestedInvoicesQuery(
    { entityId, transactionId },
    { skip: !entityId || !transactionId }
  );

  const suggestedBills = data?.suggested_invoices || [];

  const selectedRows = useMemo(
    () =>
      selectedBills.reduce((acc, bill) => {
        acc[bill.uuid] = true;
        return acc;
      }, {} as Record<string, boolean>),
    [selectedBills]
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "select",
        size: 2,
        enableSorting: false,
        header: () => <></>,
        cell: ({ row }) => {
          const isDisabled = !row.getCanSelect();
          return (
            <ConditionalToolTip
              condition={
                isDisabled
                  ? "The total amount of selected bills has already exceeded the transaction amount"
                  : false
              }
            >
              <span>
                <Checkbox
                  checked={row.getIsSelected()}
                  disabled={isDisabled}
                  indeterminate={row.getIsSomeSelected()}
                  key={row.id}
                />
              </span>
            </ConditionalToolTip>
          );
        },
      }),

      columnHelper.accessor("invoice_date", {
        id: "BILL_DATE",
        size: 16,
        header: "Bill Date",
        enableSorting: false,
        cell: (info) => (
          <div className="t-text-body-sm t-font-medium t-text-text-60">
            {info.getValue()
              ? dayjs(info.getValue()).format(DISPLAY_DATE)
              : "-"}
          </div>
        ),
      }),

      columnHelper.accessor("invoice_number", {
        id: "BILL_NUMBER",
        size: 16,
        header: "BILL NUMBER",
        enableSorting: false,
        cell: (info) => (
          <ConditionalLink
            to={link(
              `/books/bills-and-vendors/bill/${info.row.original.invoice_uuid}`,
              {
                moreQuery: { BILL_TYPE: FINANCIAL_RECORD_TYPE.BILL },
              }
            )}
          >
            <Button customType={info.getValue() ? "link" : "ghost"}>
              {info.getValue() || "-"}
            </Button>
          </ConditionalLink>
        ),
      }),

      columnHelper.accessor("customer_name", {
        id: "VENDOR",
        size: 26,
        header: "Vendor",
        enableSorting: false,
        cell: (info) => (
          <div className="t-text-body-sm t-font-medium t-text-text-60">
            {info.getValue() || "-"}
          </div>
        ),
      }),

      columnHelper.accessor("match_score", {
        id: "match_percentage",
        size: 20,
        header: "Match percentage",
        enableSorting: false,
        cell: (info) => {
          const matchScore = info.getValue() || 0;
          const tagType = matchScore > 80 ? "green" : "yellow";
          return (
            <Tag tagType={tagType} size="small" rounded icon={false}>
              {Math.round(matchScore)}%
            </Tag>
          );
        },
      }),

      columnHelper.accessor("due_balance", {
        id: "DUE_BALANCE",
        size: 16,
        header: () => (
          <span className="t-flex t-justify-end t-ml-auto">Amount</span>
        ),
        enableSorting: false,
        cell: (info) => (
          <div className="t-text-right t-text-body-sm t-font-medium t-text-text-60">
            <AmountSuperScript
              amount={getUsdPendingAmount(info.row.original)}
            />
          </div>
        ),
      }),
    ],
    [link]
  );

  const table = useReactTable({
    data: suggestedBills,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: (row) => {
      const isSelected = selectedRows[row.original.invoice_uuid];
      return isSelected ? true : enableSelection;
    },
    state: { rowSelection: selectedRows },
    getRowId: (row) => row.invoice_uuid,
    enableMultiRowSelection: true,
    defaultColumn: { minSize: 1, maxSize: 100 },
  });

  const onRowClick = (row: Row<SuggestedInvoice>) => {
    if (!row.getCanSelect()) return;

    const isRowSelected = selectedBills.find(
      (bill) => bill.uuid === row.original.invoice_uuid
    );

    if (isRowSelected) {
      setSelectedBills((prev) =>
        prev.filter((bill) => bill.uuid !== row.original.invoice_uuid)
      );
      return;
    }

    const convertedBill: Partial<VendorBill> &
      Pick<
        VendorBill,
        "uuid" | "bill_number" | "due_balance" | "pending_amount"
      > = {
      uuid: row.original.invoice_uuid,
      bill_number: row.original.invoice_number,
      due_balance: String(row.original.due_balance),
      pending_amount: row.original.pending_amount,
      currency: row.original.currency,
      payment_record_amount: row.original.payment_record_amount ?? undefined,
    };

    setSelectedBills((prev) => [...prev, convertedBill as VendorBill]);
  };

  if (suggestedBills.length === 0) {
    return null;
  }

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <Accordion.Root
        type="single"
        collapsible
        value={accordionValue}
        onValueChange={setAccordionValue}
        className="t-space-y-4"
      >
        <Accordion.Item value={SUGGESTED_ACCORDION_VALUE}>
          <Accordion.Trigger arrowPosition="left">
            <div className="t-flex t-items-center t-justify-between t-w-full">
              <div className="t-flex t-items-center t-gap-2">
                Suggested bill matches
                <Tag tagType="purple" size="small" rounded icon={false}>
                  <img
                    src={AIGenerateButtonStar}
                    alt="AI"
                    className="t-w-3 t-h-3 t-mr-1"
                  />
                  AI
                </Tag>
              </div>
              <span className="t-flex t-items-center t-gap-1 t-text-body-sm t-text-text-30">
                <img
                  src={TicketResolve}
                  alt="TicketResolve"
                  className="t-size-3"
                />
                {pluralize(suggestedBills.length, "match", "matches")} found
              </span>
            </div>
          </Accordion.Trigger>
          <Accordion.Content>
            <TableUI
              height="auto"
              table={table}
              size="regular"
              onRowClick={onRowClick}
            />
          </Accordion.Content>
        </Accordion.Item>
      </Accordion.Root>

      {accordionValue === SUGGESTED_ACCORDION_VALUE && (
        <div className="t-flex t-justify-start">
          <Button
            customType="link_neutral"
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setAccordionValue(undefined);
            }}
          >
            View all bills
            <ArrowLightLine />
          </Button>
        </div>
      )}
    </div>
  );
};
